/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { add, format, startOfWeek } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

// TODO: we can't migrate UIDatePicker to Carbon yes because it doesn't support month selection
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

import { Option, Select as CarbonSelect, OptionGroupHeader } from 'carbon-react/lib/components/select';
import DateInput from 'carbon-react/lib/components/date';
import { forceLocalDate } from '@futrli/predict/utils/dates';
import Textbox from 'carbon-react/lib/components/textbox';
import Box from 'carbon-react/lib/components/box';
import Icon from 'carbon-react/lib/components/icon';

import { useI18nConstants } from '../../../use-i18n-constants';
import { DATE_FORMAT, DEFAULT_PERIOD_NUMBER, defaultCalendarOptions } from '../../../constants';
import { useReportBuilderContext } from '../../../report-builder.context';
import { CARD_TYPES, COMPARISON_OPTIONS, CalendarOptions, PERIOD_OPTIONS } from '../../../types';
import {
  formatQuarterDateStr,
  getCurrentQuarter,
  getQuarterOptions,
  getSelectedQuarter,
  getYearOptions
} from '../../../utils-dates';

import styles from './builder-panel.scss';

const getPeriodsNumber = (calOptions?: CalendarOptions) => {
  if (typeof calOptions?.periods === 'number') {
    return calOptions.periods;
  }

  return DEFAULT_PERIOD_NUMBER;
};
const DAYJS_DATE_FORMAT = 'YYYY-MM-DD';
const DAYJS_MONTH_FORMAT = 'MMMM YYYY';
const DROPDOWN_MAX_HEIGHT = 800;

export const PeriodSelector = () => {
  const intl = useIntl();
  const { setCalendarOptions, currentCard, financialConfig } = useReportBuilderContext();
  const { comparisonOptions } = useI18nConstants();
  const calOptions = (currentCard && currentCard[CARD_TYPES.WIDGET]?.calendarOptions) || undefined;
  const [startDate, setStartDate] = useState(calOptions?.startDate || PERIOD_OPTIONS.THIS_PERIOD);
  const [selectedYear, setSelectedYear] = useState(
    startDate !== PERIOD_OPTIONS.THIS_PERIOD && startDate !== PERIOD_OPTIONS.LAST_PERIOD
      ? Number(startDate.split('-')[0])
      : new Date().getFullYear()
  );
  const [selectedQuarter, setSelectedQuarter] = useState(
    financialConfig && calOptions?.startDate ? getSelectedQuarter(calOptions.startDate, financialConfig) : ''
  );
  const [periodIndicator, setPeriodIndicator] = useState<PERIOD_OPTIONS>(
    startDate === PERIOD_OPTIONS.THIS_PERIOD || startDate === PERIOD_OPTIONS.LAST_PERIOD
      ? startDate
      : PERIOD_OPTIONS.OTHER
  );
  const [comparisonPeriod, setComparisonPeriod] = useState(calOptions?.comparision || COMPARISON_OPTIONS.PREVIOUS);
  const [periodsNumber, setPeriodsNumber] = useState(getPeriodsNumber(calOptions));
  const [periodsNumberValue, setPeriodsNumberValue] = useState(getPeriodsNumber(calOptions));
  const [ref, setRef] = useState(calOptions?.type || defaultCalendarOptions.type);
  const [dateType, setDateType] = useState(
    `${calOptions?.type || defaultCalendarOptions.type}#${
      startDate === PERIOD_OPTIONS.THIS_PERIOD || startDate === PERIOD_OPTIONS.LAST_PERIOD ? startDate : 'custom'
    }`
  );
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const periodsNumberTimerId = useRef<number | null>(null);
  const selectRef = useRef<HTMLInputElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState(180);

  // Calculate available space for the dropdown list
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (selectRef.current) {
        const selectElement = selectRef.current;
        const selectRect = selectElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - selectRect.bottom;

        const availableHeight = Math.max(spaceBelow - 30, 180);
        setListMaxHeight(availableHeight < DROPDOWN_MAX_HEIGHT ? availableHeight : DROPDOWN_MAX_HEIGHT);
      }
    };

    // Calculate on mount and when component updates
    calculateMaxHeight();

    // Recalculate on window resize and scroll
    window.addEventListener('resize', calculateMaxHeight);

    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  const pastPeriodOptions = useMemo(
    () => [
      { name: intl.formatMessage({ id: 'common.yesterday', defaultMessage: 'Yesterday' }), value: 'daily' },
      {
        name: intl.formatMessage({ id: 'reporting.period.last_week_mon_sun', defaultMessage: 'Last week (Mon-Sun)' }),
        value: 'weekly_mon'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.last_week_sun_sat', defaultMessage: 'Last week (Sun-Sat)' }),
        value: 'weekly_sun'
      },
      {
        name: intl.formatMessage({ id: 'common.last_month', defaultMessage: 'Last month' }),
        value: 'monthly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.last_quarter', defaultMessage: 'Last quarter' }),
        value: 'quarterly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.last_fin_year', defaultMessage: 'Last financial year' }),
        value: 'yearly'
      }
    ],
    [intl]
  );
  const currentPeriodOptions = useMemo(
    () => [
      { name: intl.formatMessage({ id: 'common.today', defaultMessage: 'Today' }), value: 'daily' },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_week_mon_sun', defaultMessage: 'This week (Mon-Sun)' }),
        value: 'weekly_mon'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_week_sun_sat', defaultMessage: 'This week (Sun-Sat)' }),
        value: 'weekly_sun'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_month', defaultMessage: 'This month' }),
        value: 'monthly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_quarter', defaultMessage: 'This quarter' }),
        value: 'quarterly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_fin_year', defaultMessage: 'This financial year' }),
        value: 'yearly'
      }
    ],
    [intl]
  );
  const customPeriodOptions = useMemo(
    () => [
      { name: intl.formatMessage({ id: 'reporting.period.custom_day', defaultMessage: 'Custom day' }), value: 'daily' },
      {
        name: intl.formatMessage({
          id: 'reporting.period.custom_week_mon_sun',
          defaultMessage: 'Custom week (Mon-Sun)'
        }),
        value: 'weekly_mon'
      },
      {
        name: intl.formatMessage({
          id: 'reporting.period.custom_week_sun_sat',
          defaultMessage: 'Custom week (Sun-Sat)'
        }),
        value: 'weekly_sun'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.custom_month', defaultMessage: 'Custom month' }),
        value: 'monthly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.custom_quarter', defaultMessage: 'Custom quarter' }),
        value: 'quarterly'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.custom_fin_year', defaultMessage: 'Custom financial year' }),
        value: 'yearly'
      }
    ],
    [intl]
  );

  const onDateTypeChange = (event: ChangeEvent<{ value: unknown }>) => {
    const newDateType = event.target.value as string;
    setDateType(newDateType);
    if (newDateType.includes('#')) {
      const dateBites = newDateType.split('#');
      setRef(dateBites[0]);
      setPeriodIndicator(dateBites[1] === 'custom' ? PERIOD_OPTIONS.OTHER : (dateBites[1] as PERIOD_OPTIONS));
      if (dateBites[1] === 'custom') {
        setStartDate('');
      }
    }
  };

  // setting default calendar options if it's undefined
  useEffect(() => {
    if (!calOptions && currentCard) {
      setCalendarOptions(defaultCalendarOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calOptions, currentCard]);

  useEffect(() => {
    if (
      startDate &&
      calOptions &&
      (calOptions.startDate !== startDate ||
        calOptions.comparision !== comparisonPeriod ||
        calOptions.periods !== periodsNumber ||
        calOptions.type !== ref)
    ) {
      setCalendarOptions({
        type: ref,
        startDate,
        comparision: comparisonPeriod,
        periods: periodsNumber
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, periodsNumber, comparisonPeriod, startDate, calOptions]);

  useEffect(() => {
    if (financialConfig && ref === 'quarterly' && !calOptions?.startDate) {
      setStartDate(format(getCurrentQuarter(financialConfig), DATE_FORMAT));
    }
  }, [financialConfig, ref, calOptions?.startDate]);

  useEffect(() => {
    if (periodIndicator === PERIOD_OPTIONS.THIS_PERIOD || periodIndicator === PERIOD_OPTIONS.LAST_PERIOD) {
      setStartDate(periodIndicator);
    } else if (!startDate) {
      const dateString = format(new Date(), DATE_FORMAT);
      switch (ref) {
        case 'weekly_mon':
          setStartDate(format(add(startOfWeek(new Date(), { weekStartsOn: 0 }), { days: 1 }), DATE_FORMAT));
          break;
        case 'weekly_sun':
          setStartDate(format(add(startOfWeek(new Date(), { weekStartsOn: 6 }), { days: 1 }), DATE_FORMAT));
          break;
        case 'monthly':
          setStartDate(`${dateString.substring(0, dateString.lastIndexOf('-'))}-01`);
          break;
        case 'quarterly':
          if (financialConfig) {
            const currentQuarter = getCurrentQuarter(financialConfig);
            setStartDate(format(currentQuarter, DATE_FORMAT));
            setSelectedQuarter(format(currentQuarter, DATE_FORMAT));
          }
          break;
        case 'yearly':
          if (financialConfig) {
            setStartDate(financialConfig.current_year.end);
            setSelectedYear(new Date(financialConfig.current_year.end).getFullYear());
          }
          break;
        default:
          setStartDate(dateString);
          break;
      }
    }
  }, [periodIndicator, financialConfig, ref, startDate]);

  const handleYearChange = (event: ChangeEvent<{ value: unknown }>) => {
    const newYear = +(event.target.value as string);
    setSelectedYear(newYear);
    if (financialConfig) {
      const finYearEnd = financialConfig.current_year.end;
      const dateMonth = finYearEnd.substring(finYearEnd.indexOf('-') + 1);
      setStartDate(`${newYear}-${dateMonth}`);
    }
  };

  const handleStartDateChange = (date: unknown) => {
    setStartDate((date as Dayjs).format('YYYY-MM-DD'));
  };

  const handleComparisonChange = (event: ChangeEvent<{ value: unknown }>) => {
    setComparisonPeriod(event.target.value as COMPARISON_OPTIONS);
  };

  const handlePeriodsNumberChange = (value: string) => {
    let numericValue = +value;
    if (isNaN(numericValue) || numericValue < 0) {
      numericValue = 0;
    }

    setPeriodsNumberValue(numericValue);
    // using a timer so that input change won't be directly tied to state change
    // thus making it faster.
    periodsNumberTimerId.current && clearTimeout(periodsNumberTimerId.current);
    periodsNumberTimerId.current = (setTimeout(() => setPeriodsNumber(numericValue), 500) as unknown) as number;
  };

  const handleQuarterChange = (event: ChangeEvent<{ value: unknown }>) => {
    setSelectedQuarter(event.target.value as string);
    setStartDate(event.target.value as string);
  };

  const disableDates = (date: Dayjs) => {
    if (ref === 'monthly') {
      return false;
    } else if (ref === 'weekly_mon') {
      return date.day() !== 1;
    } else {
      return date.day() !== 0;
    }
  };

  const showStartDatePicker =
    periodIndicator === PERIOD_OPTIONS.OTHER && ['monthly', 'weekly_mon', 'weekly_sun'].includes(ref);

  const showYearPicker = ref === 'yearly' && periodIndicator === PERIOD_OPTIONS.OTHER;

  const showQuarterPicker = ref === 'quarterly' && periodIndicator === PERIOD_OPTIONS.OTHER && !!financialConfig;

  const handleOpenDatePicker = () => {
    setOpenDatePicker(true);
  };
  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };

  return calOptions ? (
    <div className={clsx(styles.periodSelector)}>
      <h3>
        <FormattedMessage id="reporting.date_range" defaultMessage="Date range" />
      </h3>

      <CarbonSelect
        id="period-select"
        onChange={onDateTypeChange}
        value={dateType}
        size="small"
        mb={1}
        aria-label={intl.formatMessage({
          id: 'reporting.period.date_range_options_list',
          defaultMessage: 'Date range options list'
        })}
        listMaxHeight={listMaxHeight}
        ref={selectRef}
      >
        {currentPeriodOptions.map(period => (
          <Option key={`${period.value}#this_period`} value={`${period.value}#this_period`} text={period.name} />
        ))}
        <OptionGroupHeader label="—————————————————————————————————————" />
        {pastPeriodOptions.map(period => (
          <Option key={`${period.value}#last_period`} value={`${period.value}#last_period`} text={period.name} />
        ))}
        <OptionGroupHeader label="—————————————————————————————————————" />
        {customPeriodOptions.map(period => (
          <Option key={`${period.value}#custom`} value={`${period.value}#custom`} text={period.name} />
        ))}
      </CarbonSelect>

      {dateType.includes('#custom') && (
        <>
          {ref === 'daily' && (
            <div className={clsx(styles.datePicker)}>
              <DateInput
                id="date-selector"
                aria-label={intl.formatMessage({
                  id: 'common.select_custom_date.aria_label',
                  defaultMessage: 'Select custom date'
                })}
                value={startDate ? format(new Date(startDate), DATE_FORMAT) : ''}
                onChange={e => handleStartDateChange(dayjs(forceLocalDate(e.target.value.rawValue || '')))}
                disablePortal={false}
                maxWidth="306px"
                size="small"
              />
            </div>
          )}
          {showStartDatePicker && (
            <div className={clsx(styles.datePickerMui)}>
              {/* TODO: update to date picker once we can select custom months without selecting specific date */}
              <DatePicker
                onChange={handleStartDateChange}
                inputFormat={ref === 'monthly' ? DAYJS_MONTH_FORMAT : DAYJS_DATE_FORMAT}
                value={dayjs(new Date(startDate))}
                views={ref === 'monthly' ? ['year', 'month'] : ['year', 'day']}
                openTo={ref === 'monthly' ? 'month' : 'day'}
                shouldDisableDate={disableDates}
                open={openDatePicker}
                onOpen={handleOpenDatePicker}
                onClose={handleCloseDatePicker}
                renderInput={params => (
                  <TextField {...params} onClick={handleOpenDatePicker} style={{ cursor: 'pointer' }} />
                )}
                InputProps={{
                  endAdornment: (
                    <button className={styles.transparentBtn}>
                      <Icon type="calendar" />
                    </button>
                  )
                }}
              />
            </div>
          )}
          {showYearPicker && (
            <CarbonSelect
              id="year-select"
              onChange={handleYearChange}
              value={`${selectedYear}`}
              size="small"
              aria-label={intl.formatMessage({
                id: 'common.year_options.aria_label',
                defaultMessage: 'Year options list'
              })}
              mb={1}
            >
              {getYearOptions().map(option => (
                <Option key={option} value={`${option}`} text={`${option}`} />
              ))}
            </CarbonSelect>
          )}
          {showQuarterPicker && (
            <CarbonSelect
              id="quarter-select"
              onChange={handleQuarterChange}
              value={selectedQuarter}
              size="small"
              aria-label={intl.formatMessage({
                id: 'common.quarter_options.aria_label',
                defaultMessage: 'Quarter options list'
              })}
              mb={1}
            >
              {getQuarterOptions(financialConfig).map(option => (
                <Option
                  key={`${option.getFullYear()}-${option.getMonth() + 1}-${option.getDate()}`}
                  value={format(option, DATE_FORMAT)}
                  text={formatQuarterDateStr(option)}
                />
              ))}
            </CarbonSelect>
          )}
        </>
      )}

      <Box display="flex">
        <Box width={0.6}>
          <CarbonSelect
            id="period-direction"
            onChange={handleComparisonChange}
            value={comparisonPeriod}
            size="small"
            aria-label={intl.formatMessage({
              id: 'common.period_direction.aria_label',
              defaultMessage: 'Period direction options list'
            })}
            mb={1}
            mr={1}
          >
            {comparisonOptions.map(option => (
              <Option
                key={option.value}
                value={option.value}
                text={`${intl.formatMessage({ id: 'common.and', defaultMessage: 'And' })} ${option.name}`}
              />
            ))}
          </CarbonSelect>
        </Box>
        <Box width={0.4} className={styles.periodsInput}>
          <Textbox
            id="periods-input"
            onChange={e => {
              if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
              handlePeriodsNumberChange(e.target.value || '');
            }}
            label={intl.formatMessage({ id: 'common.periods', defaultMessage: 'Periods' })}
            value={`${periodsNumberValue}`}
            labelInline
            inputWidth={45}
            size="small"
            min={0}
          />
        </Box>
      </Box>
    </div>
  ) : null;
};
